import type {
  ApiQuestionnaireSubmission,
  SubmitQuestionnaireAnswersArgs,
} from '../schemas/questionnaireSubmissions.schema';
import type { BaseApiResponse } from './types/api.types';

import { mapQuestionnaireSubmissionFromApi } from './mappers/questionnaireSubmissions.mappers';
import { post, get } from './axios';

const baseUrl = '/v2/questionnaire-submissions';

export async function fetchPendingQuestionnaireSubmissions() {
  const response =
    await get<BaseApiResponse<ApiQuestionnaireSubmission[]>>(baseUrl);
  return (response?.data ?? []).map(mapQuestionnaireSubmissionFromApi);
}

// Org-scoped mount when there is an organization, exactly like
// `fetchQuestionnaireSubmissionById` below: the organization has to be in the
// path, because that is what the guard narrows `questionnaires_read` against.
// Passed as a query parameter it was checked against the caller's last used
// organization instead — a trainer whose role here lacks the permission still
// got through on the strength of holding it somewhere else.
export async function fetchQuestionnaireSubmissionsForUser({
  organizationId,
  userId,
}: {
  organizationId?: string;
  userId: string;
}) {
  const url = organizationId
    ? `/v2/organizations/${organizationId}/questionnaire-submissions/user/${userId}`
    : `${baseUrl}/user/${userId}`;
  const response =
    await get<BaseApiResponse<ApiQuestionnaireSubmission[]>>(url);
  return (response?.data ?? []).map(mapQuestionnaireSubmissionFromApi);
}

export async function fetchQuestionnaireSubmissionById({
  organizationId,
  id,
}: {
  organizationId?: string;
  id: string;
}) {
  const url = organizationId
    ? `/v2/organizations/${organizationId}/questionnaire-submissions/${id}`
    : `${baseUrl}/${id}`;
  const response = await get<BaseApiResponse<ApiQuestionnaireSubmission>>(url);
  return response?.data
    ? mapQuestionnaireSubmissionFromApi(response.data)
    : null;
}

export async function submitQuestionnaireAnswers({
  id,
  answers,
}: SubmitQuestionnaireAnswersArgs) {
  const response = await post<ApiQuestionnaireSubmission>(
    `${baseUrl}/${id}/answers`,
    { answers },
  );
  return response?.data ?? null;
}
